import type { ContactTranslations } from './translations.types'

export const englishTranslations: ContactTranslations = {
  schema: {
    name: {
      required: 'Name is required',
      maxLength: 'Name cannot exceed 200 characters',
    },
    email: {
      invalidFormat: 'Invalid email format',
      maxLength: 'Email cannot exceed 256 characters',
    },
    address: {
      maxLength: 'Address cannot exceed 500 characters',
    },
    phone: {
      countryRequired: 'Country is required',
      areaCodeMaxLength: 'Area code cannot exceed 3 digits',
      numberRequired: 'Phone number is required',
      numberMaxLength: 'Phone number cannot exceed 10 digits',
      extensionMaxLength: 'Extension cannot exceed 3 digits',
    },
    socialNetwork: {
      typeRequired: 'Please select a social network type',
      urlRequired: 'Profile URL is required',
      urlMaxLength: 'URL cannot exceed 2048 characters',
    },
    useFor: {
      required: 'The contact must have at least one use',
    },
  },
  modal: {
    titlePrefix: 'Contact',
    actionBadge: {
      creating: 'Creating',
      editing: 'Editing',
    },
    description: 'Contact form',
    button: {
      save: 'Save',
      update: 'Update',
      cancel: 'Cancel',
      close: 'Close',
      addPhone: 'Add phone',
      addSocialNetwork: 'Add social network',
    },
    label: {
      name: 'Name',
      email: 'Email',
      address: 'Address',
      description: 'Description',
      useFor: 'Use for',
      country: 'Country',
      state: 'State/Department',
      city: 'City',
      neighborhood: 'Neighborhood',
      category: 'Category',
      phoneNumber: 'Number',
      socialType: 'Social network',
      socialUrl: 'URL',
    },
    placeholder: {
      name: 'Contact name',
      address: 'Contact address',
      description: 'Contact description',
      select: '\u2014 Select \u2014',
      loading: 'Loading...',
      socialUrl: 'https://...',
    },
    tab: {
      basicData: 'Basic data',
      location: 'Location',
      phone: 'Phone',
      socialNetwork: 'Social networks',
    },
    section: {
      location: 'Location',
      phones: 'Phones',
      socialNetworks: 'Social Networks',
    },
    error: {
      preventRemoval: 'There must be at least one Default contact',
      transferBlocked: 'Cannot transfer. The current default contact has no other uses and would be left without any.',
      neighborhoodMismatch: 'The selected neighborhood does not belong to the indicated city',
      catalogsPhone: 'Error loading catalogs. Country options may not be available.',
      catalogsSocial: 'Error loading catalogs. Social network types may not be available.',
    },
    aria: {
      phoneCategory: (index: number) => `Phone ${index + 1} category`,
      phoneCountry: (index: number) => `Phone ${index + 1} country`,
      removePhone: (index: number) => `Remove phone ${index + 1}`,
      socialType: (index: number) => `Social network ${index + 1} type`,
      socialUrl: (index: number) => `Social network ${index + 1} URL`,
      removeSocial: (index: number) => `Remove social network ${index + 1}`,
      country: 'Country',
      state: 'State/Department',
      city: 'City',
      neighborhood: 'Neighborhood',
    },
    defaultCategory: 'Mobile',
    emptyState: {
      phone: 'No records. Click "Add phone" to get started.',
      socialNetwork: 'No records. Click "Add social network" to get started.',
    },
    column: {
      actions: 'Actions',
    },
  },
  grid: {
    title: 'Contact',
    button: { addContact: 'Add contact' },
    aria: { table: 'Contacts table' },
    column: {
      name: 'Name',
      email: 'Email',
      useFor: 'Use for',
      phone: 'Phone',
      socialNetworks: 'Social networks',
      actions: 'Actions',
    },
    pagination: {
      previous: 'Previous',
      next: 'Next',
    },
    emptyBanner: "No records. Click 'Add contact' to get started.",
    socialNetworkNotEnabled: 'Social network not enabled',
  },
  actions: {
    pendingDelete: 'Pending deletion',
    undo: 'Undo',
    viewDetail: 'View Detail',
    edit: 'Edit',
    delete: 'Delete',
  },
  emptyState: {
    noContacts: 'No contacts registered.',
    addHint: " Click 'Add Contact' to get started.",
    addButton: 'Add Contact',
  },
  pillEmail: {
    error: {
      invalidFormat: 'Invalid email format',
      duplicate: 'This email has already been added',
    },
    placeholder: 'Enter email and press Enter...',
    aria: {
      list: 'Email addresses',
      emailLabel: (email: string) => `Email: ${email}`,
      removeEmail: (email: string) => `Remove ${email}`,
      addEmail: 'Add email address',
    },
  },
  phoneInput: {
    placeholder: {
      area: 'Area',
      number: 'Phone number',
      extension: 'Ext.',
    },
    aria: {
      group: 'Phone number: area, number, extension',
      areaCode: 'Area code',
      number: 'Number',
      extension: 'Extension',
    },
  },
  tagSelector: {
    selectAll: 'Use for all',
    aria: {
      group: 'Use for',
      option: (label: string) => `Use for: ${label}`,
    },
  },
  manager: {
    defaultLabel: 'Default',
  },
  dialog: {
    default: {
      title: 'Change default contact',
      newContactFallback: 'the new contact',
      message: (currentName: string, newName: string) =>
        `Contact \u201C${currentName}\u201D is already marked as Default. Do you want to transfer the default to \u201C${newName}\u201D?`,
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    delete: {
      title: 'Delete',
      message: 'Are you sure you want to delete this record?',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    deleteDefault: {
      title: 'Delete default contact',
      message: (defaultName: string, newDefaultName: string) =>
        `Contact \u201C${defaultName}\u201D is the default. Deleting it will transfer the default to \u201C${newDefaultName}\u201D. Do you want to continue?`,
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
  },
  skeleton: {
    column: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      actions: 'Actions',
    },
  },
  enums: {
    useFor: { 0: 'Default' },
    phoneCategory: { 0: 'Mobile' },
  },
}
